<script setup>
import { podeReagir } from 'dashboard/helper/podeReagir';
import { onMounted, computed, ref, toRef, toRefs } from 'vue';
import { useStore } from 'vuex';
import { useTimeoutFn } from '@vueuse/core';
import { provideMessageContext } from './provider.js';
import { useTrack, useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { emitter } from 'shared/helpers/mitt';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { LocalStorage } from 'shared/helpers/localStorage';
import { ACCOUNT_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  MESSAGE_TYPES,
  ATTACHMENT_TYPES,
  MESSAGE_VARIANTS,
  SENDER_TYPES,
  ORIENTATION,
  MESSAGE_STATUS,
  CONTENT_TYPES,
} from './constants';

import Avatar from 'next/avatar/Avatar.vue';
import { corDoNome } from 'dashboard/helper/avatar';

import TextBubble from './bubbles/Text/Index.vue';
import ActivityBubble from './bubbles/Activity.vue';
import CartaoDeAssinatura from 'dashboard/components-next/assinatura/CartaoDeAssinatura.vue';
import ImageBubble from './bubbles/Image.vue';
import FileBubble from './bubbles/File.vue';
import AudioBubble from './bubbles/Audio.vue';
import VideoBubble from './bubbles/Video.vue';
import EmbedBubble from './bubbles/Embed.vue';
import InstagramStoryBubble from './bubbles/InstagramStory.vue';
import EmailBubble from './bubbles/Email/Index.vue';
import UnsupportedBubble from './bubbles/Unsupported.vue';
import ContactBubble from './bubbles/Contact.vue';
import DesfazerEnvio from './DesfazerEnvio.vue';
import { useEnvioSegurado } from './useEnvioSegurado';
import DyteBubble from './bubbles/Dyte.vue';
import LocationBubble from './bubbles/Location.vue';
import CSATBubble from './bubbles/CSAT.vue';
import FormBubble from './bubbles/Form.vue';
import VoiceCallBubble from './bubbles/VoiceCall.vue';

import MessageError from './MessageError.vue';
import ContextMenu from 'dashboard/modules/conversations/components/MessageContextMenu.vue';
import ForwardedIndicator from './ForwardedIndicator.vue';
import MessageReactions from './MessageReactions.vue';
import ReactionPicker from './ReactionPicker.vue';
import { useBranding } from 'shared/composables/useBranding';
import {
  podeEditarMensagem,
  ehInboxWhatsApp as ehInboxWhatsAppHelper,
} from 'dashboard/helper/messageEditability';

/**
 * @typedef {Object} Attachment
 * @property {number} id - Unique identifier for the attachment
 * @property {number} messageId - ID of the associated message
 * @property {'image'|'audio'|'video'|'file'|'location'|'fallback'|'share'|'story_mention'|'contact'|'ig_reel'} fileType - Type of the attachment (file or image)
 * @property {number} accountId - ID of the associated account
 * @property {string|null} extension - File extension
 * @property {string} dataUrl - URL to access the full attachment data
 * @property {string} thumbUrl - URL to access the thumbnail version
 * @property {number} fileSize - Size of the file in bytes
 * @property {number|null} width - Width of the image if applicable
 * @property {number|null} height - Height of the image if applicable
 */

/**
 * @typedef {Object} Sender
 * @property {Object} additional_attributes - Additional attributes of the sender
 * @property {Object} custom_attributes - Custom attributes of the sender
 * @property {string} email - Email of the sender
 * @property {number} id - ID of the sender
 * @property {string|null} identifier - Identifier of the sender
 * @property {string} name - Name of the sender
 * @property {string|null} phone_number - Phone number of the sender
 * @property {string} thumbnail - Thumbnail URL of the sender
 * @property {string} type - Type of sender
 */

/**
 * @typedef {Object} ContentAttributes
 * @property {string} externalError - an error message to be shown if the message failed to send
 */

/**
 * @typedef {Object} Props
 * @property {('sent'|'delivered'|'read'|'failed'|'progress')} status - The delivery status of the message
 * @property {ContentAttributes} [contentAttributes={}] - Additional attributes of the message content
 * @property {Attachment[]} [attachments=[]] - The attachments associated with the message
 * @property {Sender|null} [sender=null] - The sender information
 * @property {boolean} [private=false] - Whether the message is private
 * @property {number|null} [senderId=null] - The ID of the sender
 * @property {number} createdAt - Timestamp when the message was created
 * @property {number} currentUserId - The ID of the current user
 * @property {number} id - The unique identifier for the message
 * @property {number} messageType - The type of message (must be one of MESSAGE_TYPES)
 * @property {string|null} [error=null] - Error message if the message failed to send
 * @property {string|null} [senderType=null] - The type of the sender
 * @property {string} content - The message content
 * @property {boolean} [groupWithNext=false] - Whether the message should be grouped with the next message
 * @property {Object|null} [inReplyTo=null] - The message to which this message is a reply
 * @property {boolean} [isEmailInbox=false] - Whether the message is from an email inbox
 * @property {number} conversationId - The ID of the conversation to which the message belongs
 * @property {number} inboxId - The ID of the inbox to which the message belongs
 */

// eslint-disable-next-line vue/define-macros-order
const props = defineProps({
  id: { type: Number, required: true },
  messageType: {
    type: Number,
    required: true,
    validator: value => Object.values(MESSAGE_TYPES).includes(value),
  },
  status: {
    type: String,
    required: true,
    validator: value => Object.values(MESSAGE_STATUS).includes(value),
  },
  attachments: { type: Array, default: () => [] },
  content: { type: String, default: null },
  contentAttributes: { type: Object, default: () => ({}) },
  contentType: {
    type: String,
    default: 'text',
    validator: value => Object.values(CONTENT_TYPES).includes(value),
  },
  conversationId: { type: Number, required: true },
  createdAt: { type: Number, required: true }, // eslint-disable-line vue/no-unused-properties
  currentUserId: { type: Number, required: true }, // eslint-disable-line vue/no-unused-properties
  groupWithNext: { type: Boolean, default: false },
  inboxId: { type: Number, default: null }, // eslint-disable-line vue/no-unused-properties
  inboxSupportsReplyTo: { type: Object, default: () => ({}) },
  inReplyTo: { type: Object, default: null }, // eslint-disable-line vue/no-unused-properties
  isEmailInbox: { type: Boolean, default: false },
  private: { type: Boolean, default: false },
  additionalAttributes: { type: Object, default: () => ({}) }, // eslint-disable-line vue/no-unused-properties
  sender: { type: Object, default: null },
  senderId: { type: Number, default: null },
  senderType: { type: String, default: null },
  sourceId: { type: String, default: '' }, // eslint-disable-line vue/no-unused-properties
  isSelectionMode: { type: Boolean, default: false },
  isSelected: { type: Boolean, default: false },
  isFirstInGroup: { type: Boolean, default: true },
});

const emit = defineEmits([
  'retry',
  'toggleSelection',
  'forward',
  'enterSelectionMode',
]);

const contextMenuPosition = ref({});
const showBackgroundHighlight = ref(false);
const showContextMenu = ref(false);
const { t } = useI18n();
const route = useRoute();
const inboxGetter = useMapGetter('inboxes/getInbox');
const inbox = computed(() => inboxGetter.value(props.inboxId) || {});
const { replaceInstallationName } = useBranding();

const store = useStore();
const showReactionPicker = ref(false);
const REACTION_TRIGGER_ICON = '\u{1F600}';

const reactions = computed(() => {
  return props.contentAttributes?.reactions || {};
});

const hasReactions = computed(() => {
  return Object.keys(reactions.value).length > 0;
});

// Regra de "pode reagir?" vive em `helper/podeReagir` (14/10/2026: a caixa
// da A&T virou WAME e o botão tinha sumido) — função pura, com specs.
const canReact = computed(() =>
  podeReagir({
    inbox: inbox.value,
    sourceId: props.sourceId,
    messageType: props.messageType,
    deleted: props.contentAttributes?.deleted,
    createdAt: props.createdAt,
  })
);

// Regra de "esta mensagem pode ser editada?" vive em
// `helper/messageEditability` — função pura, com specs próprios. Aqui só
// ligamos os dados do componente nela.
//
// Antes deste ponto o menu oferecia "Editar" em qualquer mensagem saída,
// inclusive nas 1.145 da produção que o backend jamais conseguiria editar.
const ehInboxWhatsApp = computed(() => ehInboxWhatsAppHelper(inbox.value));

const podeEditar = computed(() =>
  podeEditarMensagem({
    inbox: inbox.value,
    sourceId: props.sourceId,
    createdAt: props.createdAt,
  })
);

// POR QUE MOSTRAR DESABILITADO EM VEZ DE ESCONDER
//
// Esconder já custou um chamado: em 2026-08-05 o advogado mandou print
// dizendo que "Editar sumiu", e era comportamento correto — a mensagem tinha
// mais de 15 minutos. A opção some sem dizer por quê, e a conclusão natural é
// que o sistema quebrou.
//
// A janela de 15 min é regra do WhatsApp, não nossa, e não há como o usuário
// adivinhá-la. Desabilitado com o motivo ensina a regra uma vez; escondido
// gera chamado toda vez.
//
// Só vale para caixa de WhatsApp: em web/e-mail a edição é local e sempre
// funciona, então lá não existe "desabilitado" — ou aparece, ou a mensagem
// não é editável por outro motivo (apagada, falhou, é recebida).
const motivoEdicaoBloqueada = computed(() => {
  if (podeEditar.value || !ehInboxWhatsApp.value) return '';

  return t('CONVERSATION.EDIT_MESSAGE.WINDOW_HINT');
});

/**
 * Computes the message variant based on props
 * @type {import('vue').ComputedRef<'user'|'agent'|'activity'|'private'|'bot'|'template'>}
 */
const variant = computed(() => {
  if (props.private) return MESSAGE_VARIANTS.PRIVATE;

  if (props.isEmailInbox) {
    const emailInboxTypes = [MESSAGE_TYPES.INCOMING, MESSAGE_TYPES.OUTGOING];
    if (emailInboxTypes.includes(props.messageType)) {
      return MESSAGE_VARIANTS.EMAIL;
    }
  }

  if (props.contentType === CONTENT_TYPES.INCOMING_EMAIL) {
    return MESSAGE_VARIANTS.EMAIL;
  }

  if (props.status === MESSAGE_STATUS.FAILED) return MESSAGE_VARIANTS.ERROR;
  if (props.contentAttributes?.isUnsupported)
    return MESSAGE_VARIANTS.UNSUPPORTED;

  if (props.contentAttributes?.externalEcho) {
    return MESSAGE_VARIANTS.AGENT;
  }

  const isBot =
    props.sender?.type === SENDER_TYPES.AGENT_BOT ||
    props.senderType === SENDER_TYPES.AGENT_BOT ||
    (!props.sender && !props.additionalAttributes?.senderName);
  if (isBot && props.messageType === MESSAGE_TYPES.OUTGOING) {
    return MESSAGE_VARIANTS.BOT;
  }

  const variants = {
    [MESSAGE_TYPES.INCOMING]: MESSAGE_VARIANTS.USER,
    [MESSAGE_TYPES.ACTIVITY]: MESSAGE_VARIANTS.ACTIVITY,
    [MESSAGE_TYPES.OUTGOING]: MESSAGE_VARIANTS.AGENT,
    [MESSAGE_TYPES.TEMPLATE]: MESSAGE_VARIANTS.TEMPLATE,
  };

  return variants[props.messageType] || MESSAGE_VARIANTS.USER;
});

const isBotOrAgentMessage = computed(() => {
  if (props.messageType === MESSAGE_TYPES.ACTIVITY) {
    return false;
  }
  // if an outgoing message is still processing, then it's definitely a
  // message sent by the current user
  if (
    props.status === MESSAGE_STATUS.PROGRESS &&
    props.messageType === MESSAGE_TYPES.OUTGOING
  ) {
    return true;
  }
  const senderId = props.senderId ?? props.sender?.id;
  const senderType = props.sender?.type ?? props.senderType;

  if (!senderType || !senderId) {
    return true;
  }

  if (
    [SENDER_TYPES.AGENT_BOT, SENDER_TYPES.CAPTAIN_ASSISTANT].includes(
      senderType
    )
  ) {
    return true;
  }

  return senderType.toLowerCase() === SENDER_TYPES.USER.toLowerCase();
});

/**
 * Computes the message orientation based on sender type and message type
 * @returns {import('vue').ComputedRef<'left'|'right'|'center'>} The computed orientation
 */
const orientation = computed(() => {
  if (isBotOrAgentMessage.value) {
    return ORIENTATION.RIGHT;
  }

  if (props.messageType === MESSAGE_TYPES.ACTIVITY) return ORIENTATION.CENTER;

  return ORIENTATION.LEFT;
});

// 🔴 SÓ A MENSAGEM QUE ACABOU DE NASCER CHEGA COM MOVIMENTO (16/09/2026).
//
// Animar todas faria a conversa inteira "cair" a cada abertura — 80 bolhas
// em cascata para mostrar um histórico que a pessoa já conhece. O que merece
// movimento é o que ACONTECEU agora: a resposta do cliente que chegou, a
// mensagem que você acabou de mandar. Por isso a conta é feita uma vez, na
// montagem, contra a hora de criação: quem monta com menos de 8 segundos de
// vida é novidade; o histórico não.
const chegouAgora = Date.now() / 1000 - props.createdAt < 8;

const flexOrientationClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'justify-start',
    [ORIENTATION.RIGHT]: 'justify-end',
    [ORIENTATION.CENTER]: 'justify-center',
  };

  return map[orientation.value];
});

const gridClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'grid grid-cols-[32px_1fr]',
    [ORIENTATION.RIGHT]: 'grid grid-cols-[1fr_32px]',
  };

  return map[orientation.value];
});

const gridTemplate = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: `
      "avatar bubble"
      "spacer meta"
    `,
    [ORIENTATION.RIGHT]: `
      "bubble avatar"
      "meta spacer"
    `,
  };

  return map[orientation.value];
});

const shouldGroupWithNext = computed(() => {
  if (props.status === MESSAGE_STATUS.FAILED) return false;

  return props.groupWithNext;
});

// 🔴 COMO NO WHATSAPP (Victor, 17/09/2026): o rosto de quem fala fica ao
// lado da PRIMEIRA mensagem do bloco, dos dois lados — antes só as nossas
// tinham rosto, e embaixo da última. O nome vai para dentro da bolha, e a
// primeira bolha do bloco ganha a ponta que aponta para o rosto.
const shouldShowAvatar = computed(
  () => props.messageType !== MESSAGE_TYPES.ACTIVITY
);

const componentToRender = computed(() => {
  if (props.isEmailInbox && !props.private) {
    const emailInboxTypes = [MESSAGE_TYPES.INCOMING, MESSAGE_TYPES.OUTGOING];
    if (emailInboxTypes.includes(props.messageType)) return EmailBubble;
  }

  if (props.contentType === CONTENT_TYPES.INPUT_CSAT) {
    return CSATBubble;
  }

  if (
    [CONTENT_TYPES.INPUT_SELECT, CONTENT_TYPES.FORM].includes(props.contentType)
  ) {
    return FormBubble;
  }

  if (props.contentType === CONTENT_TYPES.VOICE_CALL) {
    return VoiceCallBubble;
  }

  if (props.contentType === CONTENT_TYPES.INCOMING_EMAIL) {
    return EmailBubble;
  }

  if (props.contentAttributes?.isUnsupported) {
    return UnsupportedBubble;
  }

  if (props.contentAttributes.type === 'dyte') {
    return DyteBubble;
  }

  const instagramSharedTypes = [
    ATTACHMENT_TYPES.STORY_MENTION,
    ATTACHMENT_TYPES.IG_STORY,
    ATTACHMENT_TYPES.IG_STORY_REPLY,
    ATTACHMENT_TYPES.IG_POST,
  ];
  if (instagramSharedTypes.includes(props.contentAttributes.imageType)) {
    return InstagramStoryBubble;
  }

  if (Array.isArray(props.attachments) && props.attachments.length === 1) {
    const fileType =
      props.attachments[0].fileType ?? props.attachments[0].file_type;

    if (fileType === ATTACHMENT_TYPES.AUDIO) return AudioBubble;

    if (!props.content) {
      if (fileType === ATTACHMENT_TYPES.IMAGE) return ImageBubble;
      if (fileType === ATTACHMENT_TYPES.FILE) return FileBubble;
      if (fileType === ATTACHMENT_TYPES.VIDEO) return VideoBubble;
      if (fileType === ATTACHMENT_TYPES.IG_REEL) return VideoBubble;
      if (fileType === ATTACHMENT_TYPES.EMBED) return EmbedBubble;
      if (fileType === ATTACHMENT_TYPES.LOCATION) return LocationBubble;
    }
    // Attachment content is the name of the contact
    if (fileType === ATTACHMENT_TYPES.CONTACT) return ContactBubble;
  }

  return TextBubble;
});

const shouldShowContextMenu = computed(() => {
  return !props.contentAttributes?.isUnsupported;
});

const isBubble = computed(() => {
  return props.messageType !== MESSAGE_TYPES.ACTIVITY;
});

const isMessageDeleted = computed(() => {
  return props.contentAttributes?.deleted;
});

// Apagada no advup por alguém da equipe (sem `deleted_by`, que só vem quando foi "apagar para todos" no WhatsApp):
// a pessoa ainda tem a mensagem no aparelho dela (20/10/2026, caso da mensagem na conversa errada).
// Ainda esperando para sair: a bolha fica esmaecida enquanto o cartão do "Desfazer" corre.
const { emEspera: envioEmEspera } = useEnvioSegurado(
  toRef(props, 'contentAttributes')
);

// "Desfazer envio" (20/10/2026): desfeita antes de sair — a pessoa nunca recebeu.
const envioDesfeito = computed(
  () =>
    !!(
      props.contentAttributes?.envioDesfeito ??
      props.contentAttributes?.envio_desfeito
    )
);

const apagadaSoAqui = computed(() => {
  const atributos = props.contentAttributes || {};
  return (
    !!atributos.deleted &&
    !(atributos.deletedBy ?? atributos.deleted_by) &&
    !(atributos.envioDesfeito ?? atributos.envio_desfeito) &&
    props.messageType === MESSAGE_TYPES.OUTGOING
  );
});

const isMessageEdited = computed(() => {
  return props.contentAttributes?.edited;
});

const payloadForContextMenu = computed(() => {
  return {
    id: props.id,
    content_attributes: props.contentAttributes,
    content: props.content,
    conversation_id: props.conversationId,
    // O menu precisa saber se a mensagem é do escritório (no WhatsApp oficial, a que já saiu não se apaga por aqui).
    message_type: props.messageType,
  };
});

const contextMenuEnabledOptions = computed(() => {
  const hasText = !!props.content;
  const hasAttachments = !!(props.attachments && props.attachments.length > 0);

  const isOutgoing = props.messageType === MESSAGE_TYPES.OUTGOING;
  const isFailedOrProcessing =
    props.status === MESSAGE_STATUS.FAILED ||
    props.status === MESSAGE_STATUS.PROGRESS;

  const canForwardOrSelect =
    (hasText || hasAttachments) &&
    !isFailedOrProcessing &&
    !isMessageDeleted.value &&
    !props.isSelectionMode;

  // A mensagem "cabe" no conceito de edicao: e saida, tem texto, nao falhou,
  // nao foi apagada. Se alem disso `podeEditar`, o item fica ativo; se nao,
  // aparece desabilitado com o motivo em vez de sumir.
  const edicaoAplicavel =
    isOutgoing &&
    hasText &&
    !isFailedOrProcessing &&
    !isMessageDeleted.value &&
    !props.isSelectionMode;

  return {
    copy: hasText,
    edit: edicaoAplicavel && podeEditar.value,
    editDisabled:
      edicaoAplicavel && !podeEditar.value && !!motivoEdicaoBloqueada.value,
    editDisabledReason: motivoEdicaoBloqueada.value,
    delete:
      (hasText || hasAttachments) &&
      !isFailedOrProcessing &&
      !isMessageDeleted.value,
    cannedResponse: isOutgoing && hasText && !isMessageDeleted.value,
    // advup: "Copiar link para a mensagem" e "Traduzir" removidos do menu (pedido Victor)
    copyLink: false,
    translate: false,
    replyTo:
      !props.private &&
      props.inboxSupportsReplyTo.outgoing &&
      !isFailedOrProcessing,
    forward: canForwardOrSelect,
    select: canForwardOrSelect,
  };
});

const shouldRenderMessage = computed(() => {
  const hasAttachments = !!(props.attachments && props.attachments.length > 0);
  const isEmailContentType = props.contentType === CONTENT_TYPES.INCOMING_EMAIL;
  const isUnsupported = props.contentAttributes?.isUnsupported;
  const isAnIntegrationMessage =
    props.contentType === CONTENT_TYPES.INTEGRATIONS;
  const isFailedMessage = props.status === MESSAGE_STATUS.FAILED;
  const hasExternalError = !!props.contentAttributes?.externalError;

  return (
    hasAttachments ||
    props.content ||
    isEmailContentType ||
    isUnsupported ||
    isAnIntegrationMessage ||
    isFailedMessage ||
    hasExternalError
  );
});

function openContextMenu(e) {
  showReactionPicker.value = false;
  const shouldSkipContextMenu =
    e.target?.classList.contains('skip-context-menu') ||
    ['a', 'img'].includes(e.target?.tagName.toLowerCase());
  if (shouldSkipContextMenu || getSelection().toString()) {
    return;
  }

  e.preventDefault();
  if (e.type === 'contextmenu') {
    useTrack(ACCOUNT_EVENTS.OPEN_MESSAGE_CONTEXT_MENU);
    // Clique direito: sem botão físico pra ancorar, usa o ponto do cursor.
    contextMenuPosition.value = {
      x: e.pageX || e.clientX,
      y: e.pageY || e.clientY,
    };
  } else {
    // Clique no botão de menu (chevron): ancora no próprio botão em vez do
    // ponto exato do clique — evita o menu abrir "flutuando" fora do lugar
    // quando o clique acerta uma borda do ícone pequeno (achado 2026-07-15).
    const rect = e.currentTarget.getBoundingClientRect();
    contextMenuPosition.value = {
      x: rect.left,
      y: rect.bottom + 4,
    };
  }
  showContextMenu.value = true;
}

function closeContextMenu() {
  showContextMenu.value = false;
  contextMenuPosition.value = { x: null, y: null };
}

function handleReplyTo() {
  const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
  const { conversationId, id: replyTo } = props;

  LocalStorage.updateJsonStore(replyStorageKey, conversationId, replyTo);
  emitter.emit(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, props);
}

function handleToggleSelection() {
  emit('toggleSelection', props.id);
}

function handleForward() {
  emit('forward', props.id);
}

function handleEnterSelectionMode() {
  emit('enterSelectionMode', props.id);
}

async function handleReact(emoji) {
  showReactionPicker.value = false;
  try {
    await store.dispatch('reactToMessage', {
      conversationId: props.conversationId,
      messageId: props.id,
      emoji,
    });
  } catch (error) {
    // A recusa tem frase (14/10/2026): no WhatsApp oficial a Meta pode recusar
    // (mensagem velha, fora da janela de atendimento) — clicar e nada
    // acontecer ensinava a pessoa que o botão não funciona.
    useAlert().showAlert(t('CONVERSATION.REACAO_FALHOU'));
  }
}

function toggleReactionPicker() {
  showReactionPicker.value = !showReactionPicker.value;
}

const canBeSelected = computed(() => {
  return (
    props.messageType !== MESSAGE_TYPES.ACTIVITY &&
    !props.contentAttributes?.deleted
  );
});

const avatarInfo = computed(() => {
  if (props.contentAttributes?.externalEcho) {
    const { name, avatar_url, channel_type, medium } = inbox.value;
    const iconName = avatar_url
      ? null
      : getInboxIconByType(channel_type, medium);
    return {
      name: iconName ? '' : name || t('CONVERSATION.NATIVE_APP'),
      src: avatar_url || '',
      iconName,
    };
  }

  // Outgoing message sent via external API (n8n, Digisac, third-party tools)
  if (
    props.messageType === MESSAGE_TYPES.OUTGOING &&
    props.additionalAttributes?.source === 'external_api'
  ) {
    return { name: 'Ferramenta externa', src: '' };
  }

  // If no sender, check for Slack (or other integration) sender info
  if (!props.sender) {
    const { senderName, senderAvatarUrl } = props.additionalAttributes || {};
    if (senderName) {
      return { name: senderName, src: senderAvatarUrl ?? '' };
    }
    return { name: t('CONVERSATION.BOT'), src: '' };
  }

  const { sender } = props;
  const { name, type, avatarUrl, thumbnail } = sender || {};

  // If sender type is agent bot, use avatarUrl
  if ([SENDER_TYPES.AGENT_BOT, SENDER_TYPES.CAPTAIN_ASSISTANT].includes(type)) {
    return {
      name: name ?? '',
      src: avatarUrl ?? '',
    };
  }

  // For all other senders, use thumbnail
  return {
    name: name ?? '',
    src: thumbnail ?? '',
  };
});

const avatarTooltip = computed(() => {
  if (props.contentAttributes?.externalEcho) {
    return replaceInstallationName(t('CONVERSATION.NATIVE_APP_ADVISORY'));
  }
  if (
    props.messageType === MESSAGE_TYPES.OUTGOING &&
    props.additionalAttributes?.source === 'external_api'
  ) {
    return 'Enviado por ferramenta externa ao advup';
  }
  if (avatarInfo.value.name === '') return '';
  return `${t('CONVERSATION.SENT_BY')} ${avatarInfo.value.name}`;
});

const setupHighlightTimer = () => {
  if (Number(route.query.messageId) !== Number(props.id)) {
    return;
  }

  showBackgroundHighlight.value = true;
  const HIGHLIGHT_TIMER = 1000;
  useTimeoutFn(() => {
    showBackgroundHighlight.value = false;
  }, HIGHLIGHT_TIMER);
};

onMounted(setupHighlightTimer);

const abreBloco = computed(
  () => props.isFirstInGroup && shouldShowAvatar.value
);

// O nome dentro da bolha, na cor do rosto da pessoa (a mesma de sempre,
// tirada do nome).
const nomeNoBalao = computed(() => {
  if (!props.isFirstInGroup) return '';
  if (
    [MESSAGE_VARIANTS.ACTIVITY, MESSAGE_VARIANTS.EMAIL].includes(variant.value)
  )
    return '';
  return avatarInfo.value.name || '';
});

provideMessageContext({
  ...toRefs(props),
  abreBloco,
  nomeNoBalao,
  corDoAutor: computed(() => corDoNome(nomeNoBalao.value)),
  isPrivate: computed(() => props.private),
  variant,
  orientation,
  isBotOrAgentMessage,
  shouldGroupWithNext,
  showContextMenuTrigger: computed(
    () => shouldShowContextMenu.value && isBubble.value
  ),
  openContextMenu,
});
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <div
    v-if="shouldRenderMessage"
    :id="`message${props.id}`"
    class="flex mb-2 w-full message-bubble-container group/message"
    :data-message-id="props.id"
    :class="[
      flexOrientationClass,
      {
        'msg-chega': chegouAgora,
        'group-with-next': shouldGroupWithNext,
        'bg-n-alpha-1': showBackgroundHighlight,
        'bg-n-alpha-2': isSelected,
      },
    ]"
  >
    <!-- Selection checkbox -->
    <div
      v-if="isSelectionMode && canBeSelected"
      class="flex items-center px-2 shrink-0 cursor-pointer"
      :class="{
        'order-first': orientation === ORIENTATION.LEFT,
        'order-last': orientation === ORIENTATION.RIGHT,
      }"
      @click="handleToggleSelection"
    >
      <input
        type="checkbox"
        :checked="isSelected"
        class="advup-caixa pointer-events-none"
        tabindex="-1"
      />
    </div>
    <!-- Minutas: a nota da assinatura é o cartão vivo. É nota PRIVADA (o
         cliente nunca a vê), então é checada antes da variante — senão
         viraria a bolha amarela de nota. -->
    <div v-if="contentAttributes?.data?.assinatura" class="w-full">
      <CartaoDeAssinatura :dados="contentAttributes.data.assinatura" />
    </div>
    <div v-else-if="variant === MESSAGE_VARIANTS.ACTIVITY">
      <ActivityBubble :content="content" />
    </div>
    <div
      v-else
      :class="[
        gridClass,
        {
          'gap-y-2': contentAttributes.externalError,
          'w-full': variant === MESSAGE_VARIANTS.EMAIL,
        },
      ]"
      class="gap-x-3"
      :style="{
        gridTemplateAreas: gridTemplate,
      }"
    >
      <div
        v-if="abreBloco"
        v-tooltip.left-end="avatarTooltip"
        class="[grid-area:avatar] flex items-start"
      >
        <Avatar v-bind="avatarInfo" :size="32" rounded-full />
      </div>
      <div
        class="[grid-area:bubble] flex flex-col"
        :class="{
          'ltr:ml-8 rtl:mr-8 items-end': orientation === ORIENTATION.RIGHT,
          'ltr:mr-8 rtl:ml-8': orientation === ORIENTATION.LEFT,
          'min-w-0': variant === MESSAGE_VARIANTS.EMAIL,
        }"
      >
        <ForwardedIndicator :content-attributes="contentAttributes" />
        <div
          class="flex items-end gap-1 relative group/bubble"
          :class="{ 'justify-end': orientation === ORIENTATION.RIGHT }"
          @contextmenu="openContextMenu($event)"
        >
          <!-- Reaction picker trigger for incoming messages (right side of bubble) -->
          <div
            v-if="canReact && orientation === ORIENTATION.LEFT"
            class="flex items-center order-last"
          >
            <ReactionPicker
              v-if="showReactionPicker"
              @select="handleReact"
              @close="showReactionPicker = false"
            />
            <button
              v-else
              class="w-6 h-6 flex items-center justify-center rounded-full opacity-0 group-hover/bubble:opacity-100 bg-n-alpha-1 hover:bg-n-alpha-3 transition-opacity cursor-pointer text-xs shrink-0"
              @click.stop="toggleReactionPicker"
            >
              {{ REACTION_TRIGGER_ICON }}
            </button>
          </div>

          <!-- Reaction picker trigger for outgoing messages (left side of bubble) -->
          <div
            v-if="canReact && orientation === ORIENTATION.RIGHT"
            class="flex items-center order-first"
          >
            <ReactionPicker
              v-if="showReactionPicker"
              @select="handleReact"
              @close="showReactionPicker = false"
            />
            <button
              v-else
              class="w-6 h-6 flex items-center justify-center rounded-full opacity-0 group-hover/bubble:opacity-100 bg-n-alpha-1 hover:bg-n-alpha-3 transition-opacity cursor-pointer text-xs shrink-0"
              @click.stop="toggleReactionPicker"
            >
              {{ REACTION_TRIGGER_ICON }}
            </button>
          </div>

          <div
            class="relative group transition-opacity"
            :class="{
              'line-through opacity-60': isMessageDeleted,
              'opacity-70': envioEmEspera,
            }"
          >
            <Component :is="componentToRender" />
          </div>
          <span v-if="isMessageEdited" class="msg-marca-edicao">
            {{ $t('CONVERSATION.EDIT_MESSAGE.EDITED_LABEL') }}
          </span>
          <!-- advup — pedido do Victor 2026-07-20 (estilo Digisac): mensagem
          apagada continua visível (riscada) só pra equipe interna, nunca
          vira placeholder genérico — conteúdo já preservado no backend. -->
          <span v-if="isMessageDeleted" class="msg-marca-edicao">
            {{
              envioDesfeito
                ? $t('CONVERSATION.DESFAZER_ENVIO.DESFEITA_LABEL')
                : apagadaSoAqui
                  ? $t('CONVERSATION.DELETE_MESSAGE.DELETED_ONLY_HERE_LABEL')
                  : $t('CONVERSATION.DELETE_MESSAGE.DELETED_LABEL')
            }}
          </span>
        </div>
        <!-- Desfazer envio (20/10/2026): o cartão embaixo da bolha enquanto a mensagem ainda não saiu. -->
        <DesfazerEnvio
          v-if="messageType === MESSAGE_TYPES.OUTGOING"
          :conversation-id="conversationId"
          :message-id="id"
          :content-attributes="contentAttributes"
        />
        <!-- Reactions display pills below the bubble -->
        <MessageReactions
          v-if="hasReactions"
          :reactions="reactions"
          @react="handleReact"
        />
      </div>
      <!-- Falhou sem motivo da Meta também avisa (22/10/2026): antes a bolha ficava só rosada, muda. -->
      <MessageError
        v-if="
          contentAttributes.externalError || status === MESSAGE_STATUS.FAILED
        "
        class="[grid-area:meta]"
        :class="flexOrientationClass"
        :error="contentAttributes.externalError || ''"
        @retry="emit('retry')"
      />
    </div>
    <div v-if="shouldShowContextMenu" class="context-menu-wrap">
      <ContextMenu
        v-if="isBubble"
        :context-menu-position="contextMenuPosition"
        :is-open="showContextMenu"
        :enabled-options="contextMenuEnabledOptions"
        :is-whats-app-inbox="ehInboxWhatsApp"
        :is-whats-app-oficial="!!inbox?.is_wame"
        :message="payloadForContextMenu"
        hide-button
        @open="openContextMenu"
        @close="closeContextMenu"
        @reply-to="handleReplyTo"
        @forward="handleForward"
        @select="handleEnterSelectionMode"
      />
    </div>
  </div>
</template>

<style lang="scss">
/* Mensagens seguidas do mesmo lado encostam: o canto de cima de quem
   continua falando fica fechado, como o de baixo já é — a sequência lê como
   um bloco só. */
.group-with-next + .message-bubble-container {
  .left-bubble {
    border-start-start-radius: var(--r-1);
  }

  .right-bubble {
    border-start-end-radius: var(--r-1);
  }
}

/* A chegada: sobe 8px e acende, na curva do produto. `both` para a bolha não
   piscar no lugar final antes de a animação começar. */
.message-bubble-container.msg-chega > * {
  animation: msg-chega var(--mov-3) var(--mov-curva) both;
}

.msg-marca-edicao {
  margin-inline-start: var(--esp-1);
  font-size: var(--txt-rotulo);
  font-style: italic;
  color: rgb(var(--slate-10));
  user-select: none;
}

@keyframes msg-chega {
  from {
    opacity: 0;
    translate: 0 8px;
    scale: 0.98;
  }
}
</style>
